/*
 * authz — the platform authorization catalog, SHARED across every product UI.
 *
 * No longer a mirror of the Go table, and nothing here is kept in sync by hand:
 * the catalog, the member defaults and the non-delegatable set are all DERIVED
 * from the `Permission` enum in vitesse/v1/entitlements.proto, exactly as the Go
 * side derives them. One declaration, two languages, no copy to forget — a
 * permission that exists in one and not the other is no longer representable.
 * This exists so the UI can hide/disable a mutating action the server would reject,
 * NOT to be the security boundary — the server enforces the SAME catalog and
 * rejects an unauthorized call with PermissionDenied regardless of the UI.
 *
 * Resolution order (identical to authz.go Allow):
 *   1. super-admin (admin of the Vitesse org) → yes, always;
 *   2. explicit grant (permission present in the membership);
 *   3. role→default catalog (admin: all; member: memberDefaults; other: none).
 */
import { getOption } from '@bufbuild/protobuf';
import { Permission as PermissionEnum, PermissionSchema, seed } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
import { type Identity, normalizeRole } from './identity';

export { PermissionEnum };

/** Everything the contract says about one permission. Mirrors authz.Spec. */
export interface PermissionSpec {
  readonly enum: PermissionEnum;
  /** The stored/wire form used by the grant matrix: "symbol:write". */
  readonly key: string;
  /** The `org:`-prefixed form used in checks: "org:symbol:write". */
  readonly permission: string;
  readonly memberDefault: boolean;
  readonly nonDelegatable: boolean;
  /** The key may carry a `/<instance>` suffix naming one thing it applies to;
   *  the bare key is then the wildcard — every instance. */
  readonly instanceScoped: boolean;
}

/**
 * The catalog, read off the enum's own `seed` options at module load.
 *
 * A value that declares no key is a contract bug that must not resolve to a
 * usable-looking permission — an empty key matches nothing and denies silently,
 * which is the failure the enum exists to remove. Throwing here fails the bundle
 * at import rather than one control rendering wrongly in production.
 */
export const SPECS: readonly PermissionSpec[] = PermissionSchema.values
  .filter((v) => v.number !== 0) // UNSPECIFIED is the absence of a permission
  .map((v) => {
    const s = getOption(v, seed);
    if (!s?.key) throw new Error(`authz: ${v.name} declares no (seed).key`);
    return {
      enum: v.number as PermissionEnum,
      key: s.key,
      permission: `org:${s.key}`,
      memberDefault: s.memberDefault ?? false,
      nonDelegatable: s.nonDelegatable ?? false,
      instanceScoped: s.instanceScoped ?? false,
    };
  });

/** The `org:`-prefixed permission string for an enum value. */
export function permissionOf(p: PermissionEnum): string {
  return SPECS.find((s) => s.enum === p)?.permission ?? '';
}

/** The separator between a base permission and the instance it names. Chosen
 *  because no permission key uses it — keys are colon-delimited — so a base can
 *  never be mistaken for an instance. Mirrors authz.go instanceSep. */
const INSTANCE_SEP = '/';

/** Split a possibly instance-scoped key into its base and the instance it names.
 *  An unscoped key reads whole as the base. Mirrors authz.go SplitInstance. */
export function splitInstance(key: string): { base: string; instance: string } {
  const i = key.lastIndexOf(INSTANCE_SEP);
  if (i < 0) return { base: key, instance: '' };
  return { base: key.slice(0, i), instance: key.slice(i + 1) };
}

/** Render the scoped spelling of a base key. Mirrors authz.go InstanceKey. */
export function instanceKey(base: string, instance: string): string {
  return instance ? `${base}${INSTANCE_SEP}${instance}` : base;
}

/** Resolve a stored grant key to its contract entry, accepting the instance form
 *  of a permission that declares itself scoped. Returns the BASE spec — the
 *  suffix names which thing, never a different permission. Mirrors authz.go
 *  SpecForKey. */
export function specForKey(key: string): PermissionSpec | undefined {
  const exact = SPECS.find((s) => s.key === key);
  if (exact) return exact;
  const { base, instance } = splitInstance(key);
  if (!instance) return undefined;
  const sp = SPECS.find((s) => s.key === base);
  return sp?.instanceScoped ? sp : undefined;
}

// PascalCase for the ergonomic `Perm.SymbolWrite` shape, derived rather than
// typed out. ACRONYMS keeps the few that would otherwise read as words — the
// alternative is a second hand-written list, which is the thing this file exists
// to delete.
const ACRONYMS = new Set(['AI']);
function pascal(enumName: string): string {
  return enumName
    .replace(/^PERMISSION_/, '')
    .split('_')
    .map((w) => (ACRONYMS.has(w) ? w : w.charAt(0) + w.slice(1).toLowerCase()))
    .join('');
}

/** The Vitesse-internal Clerk org. An ADMIN of THIS org is the platform
 *  super-admin (bypasses every per-org gate, sees the whole fleet). Platform
 *  constant — mirrors authz.go VitesseOrgID.
 *
 *  The id is MINTED BY THE IDENTITY PROVIDER, so it is not a name we choose: an
 *  id from another tenancy names nothing here, and a session carrying one
 *  silently becomes an ordinary caller — no fleet-wide view, nothing raised.
 *  Copy the id Clerk shows for the organization; never edit it by hand. */
export const VITESSE_ORG_ID = 'org_3EvLnFODfAZihwQUPH3Bgk00BCe';

/** Whether an org id names the Vitesse-internal org. */
export function isPlatformOrg(orgId: string): boolean {
  return orgId === VITESSE_ORG_ID;
}

// Permission identifiers — the `org:<feature>:<action>` key shape the catalog
// speaks on both sides of the wire,
// derived from the contract. These gate MUTATIONS and sensitive reads; a name is
// generated from the enum value, so adding a permission to the proto adds it here.
export const Perm: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(PermissionSchema.values.filter((v) => v.number !== 0).map((v) => [pascal(v.name), `org:${getOption(v, seed)!.key}`])),
);

/** The permission string — "org:symbol:write". What travels in
 *  claims and in stored grant matrices, and what every check takes.
 *
 *  Distinct from `PermissionEnum`, which is the typed contract value used at
 *  annotation sites. Two spellings of one vocabulary, both derived from the same
 *  enum, so they cannot describe different sets. */
export type Permission = string;

/** What a plain org MEMBER holds with no authored grant — DERIVED from each
 *  permission's own `member_default` seed, so it cannot drift from the Go table.
 *  Shift an action between member and admin by editing entitlements.proto. */
const memberDefaults: ReadonlySet<string> = new Set(SPECS.filter((s) => s.memberDefault).map((s) => s.permission));

/** Permissions that may never be bundled into a role, at any surface. */
export const NON_DELEGATABLE: ReadonlySet<string> = new Set(SPECS.filter((s) => s.nonDelegatable).map((s) => s.key));

/** Is the caller a Vitesse platform super-admin (admin of the Vitesse org)?
 *  Super-admins bypass every per-org gate. Mirrors authz.go IsSuperAdmin. */
export function isSuperAdmin(id: Identity): boolean {
  return isPlatformOrg(id.orgId) && normalizeRole(id.role) === 'admin';
}

/**
 * THE authorization check: may this identity perform `permission` in its active
 * org? In-memory, O(1). Mirrors authz.go Allow exactly. UX-only — the server
 * re-checks the same catalog and is the real boundary.
 */
export function can(id: Identity, permission: Permission): boolean {
  if (!id.userId || !id.orgId) return false;
  if (isSuperAdmin(id)) return true;
  /*
   * `permissions` is read defensively, and the reason is not tidiness.
   *
   * Unguarded, `id.permissions.has(...)` THROWS on an identity that lacks the
   * Set. The Go twin (`authz.Allow` → `c.HasPermission`) is nil-safe and denies
   * instead, so the two halves of one plane disagreed about what a malformed
   * identity means. A gate that throws is one `try { } catch { }` upstream away
   * from failing open, and it is the caller — not this function — who decides
   * how an exception is handled.
   *
   * Absent is treated as EMPTY, which is the same answer a well-formed identity
   * with no explicit permissions already gets: fall through to the role default
   * below. So this changes nothing for a correct identity and turns a crash into
   * a decision for an incorrect one.
   */
  if (id.permissions?.has(permission)) return true;
  switch (normalizeRole(id.role)) {
    case 'admin':
      return true;
    case 'member':
      return memberDefaults.has(permission);
    default:
      return false;
  }
}

/** Is the caller an ADMIN of their active org (or a super-admin)? The gate for
 *  admin-only surfaces (the fleet-admin app). UX-only; the server re-checks. */
export function isOrgAdmin(id: Identity): boolean {
  return isSuperAdmin(id) || normalizeRole(id.role) === 'admin';
}

/*
 * The per-user grant MATRIX — mirror of authz.go AllowFrom.
 *
 * An admin authors a member's matrix (FleetControl.SetUserGrants). Shape:
 * compact permission key (grantKey form, "integration:write") → granted. An
 * authored entry DECIDES (it binds org admins too); an absent entry falls to
 * can(). UX-only, like can(): the server runs the identical resolution and is
 * the real boundary — this exists so buttons grey out instead of erroring.
 *
 * THE SURFACE AXIS IS GONE. A grant used to name which frontend it applied on,
 * so "may write symbols from the app but not through the AI" was expressed in
 * every person's matrix. The AI now has its own org role instead — one
 * org-level answer rather than the same decision repeated per member — and what
 * remained (app vs fleet) was never a security boundary, because the surface
 * came from a browser origin the user chooses.
 */

/** The compact key a permission takes inside the grant matrix — the catalog key
 *  without its "org:" prefix. Mirrors authz.go GrantKey. */
export function grantKey(permission: Permission): string {
  return permission.replace(/^org:/, '');
}

/**
 * canFrom — can() with the authored matrix applied. Mirrors authz.go AllowFrom
 * exactly: super-admin → authored entry decides → can().
 */
export function canFrom(id: Identity, permission: Permission): boolean {
  if (!id.userId || !id.orgId) return false;
  if (isSuperAdmin(id)) return true;
  const entry = id.grants?.[grantKey(permission)];
  // `null` is treated as an authored REVOCATION, not as "unauthored".
  //
  // The claim is JSON produced by Go, and an empty Go slice can
  // arrive as `null` rather than `[]` — which is the revocation case, so the crash
  // would land on exactly the user an admin just restricted, on every request they
  // make. Reading it as a revocation matches what Go concludes from the same bytes;
  // falling through to the role default would silently RESTORE what was taken away.
  if (entry === null) return false;
  if (entry !== undefined) return entry;
  // An instance-scoped permission falls back to its WILDCARD: granted
  // "ai:specialist:spawn", the caller may spawn any specialist, including one
  // invented after the grant was written. After the exact key, so an explicit
  // revocation of one instance still denies it. Mirrors authz.go AllowFrom.
  const { base, instance } = splitInstance(grantKey(permission));
  if (instance) {
    const wild = id.grants?.[base];
    if (wild === null) return false;
    if (wild !== undefined) return wild;
    return can(id, `org:${base}` as Permission);
  }
  return can(id, permission);
}

/*
 * The ENUMERABLE catalog — what renders the fleet UI's per-user matrix (and any
 * future capability picker). Derived ENTIRELY from the permission keys: group =
 * the feature token, action = the action token, memberDefault from the role
 * table above — so adding a capability is ONE entry in Perm (mirroring its Go
 * const) and every consuming UI updates with zero further wiring. Labels are
 * humanized from the key by default; hosts may overlay i18n on top.
 */
export interface CatalogEntry {
  key: Permission;
  /** The feature token — 'gateway' | 'model' | 'integration' | … Groups the matrix. */
  group: string;
  /** The action token — 'create' | 'write' | 'use' | … */
  action: string;
  /** True when a plain member holds it by role default (an authored entry still
   *  overrides either way). The UI shows the resolved "Default" outcome from this. */
  memberDefault: boolean;
}

function entry(key: Permission): CatalogEntry {
  const [, group, action] = key.split(':');
  return { key, group, action, memberDefault: memberDefaults.has(key) };
}

/** Every capability, catalog order — mirrors authz.go Catalog(). */
export const CATALOG: readonly CatalogEntry[] = (Object.values(Perm) as Permission[]).map(entry);

/** Humanize a key token for display when no i18n label is provided:
 *  'gateway' → 'Gateway', 'use' → 'Use'. */
export function humanize(token: string): string {
  return token.charAt(0).toUpperCase() + token.slice(1);
}

/*
 * GLOBAL ROSTER ITEMS — one rule, one place.
 *
 * Specialists, custom providers, and MCP servers can be scoped GLOBAL (`orgId = '*'`): defined once
 * by Vitesse and merged into every org's roster. Every org may USE one; only a Vitesse super admin
 * may manage one or look at its secrets.
 *
 * This was five separate inline expressions — three spelled `!x.global || superAdmin`, one spelled
 * as the NEGATION `x.global && !superAdmin`, and one more on the server. A negated copy is the
 * clearest possible drift risk: change the rule in one place and the inverse silently disagrees.
 *
 * The server enforces the same rule independently (`config.ts` setSecret, `aiadmin.ts` read gate) —
 * this is the UI's copy, and it exists to hide affordances the server would reject, never as the
 * boundary itself.
 */

/** Whether this viewer may edit, delete, or reveal the secrets of a roster item. */
export function mayManageRosterItem(item: { global: boolean }, superAdmin: boolean): boolean {
  return !item.global || superAdmin;
}

/** Whether the item should render read-only. The inverse, defined ONCE so it cannot disagree. */
export function isRosterItemLocked(item: { global: boolean }, superAdmin: boolean): boolean {
  return !mayManageRosterItem(item, superAdmin);
}

/** Whether this viewer may CREATE global roster items at all — the super-admin management surface. */
export function mayManageGlobalRoster(superAdmin: boolean): boolean {
  return superAdmin;
}
